import type { ReactNode } from 'react';
import { fmtTime } from '../lib/time';
import { getLang, localeOf, useT } from '../lib/i18n';

/**
 * The coach's own name for a session type, cut to the part that tells
 * them apart: "Private session (60 min)" is "Private" in a list where
 * every row is a session and the length is the same every week.
 *
 * It only ever removes — the word is still the coach's, never ours.
 */
export function shortType(title: string) {
  const short = title
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\b(sessions?|занятия|занятие|тренировки|тренировка)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return short || title;
}

const PersonIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" />
  </svg>
);
const GroupIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" /><path d="M2.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
    <circle cx="17" cy="9" r="2.6" /><path d="M17 14.5c2.6 0 4.5 2 4.5 4.8" />
  </svg>
);
export const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12.5 10 17 19 7.5" />
  </svg>
);

/** Sessions grouped under their month, newest order kept as given. */
export function byMonth<T>(rows: T[], startsOf: (r: T) => string): [string, T[]][] {
  const fmt = new Intl.DateTimeFormat(localeOf(getLang()), { month: 'long', year: 'numeric' });
  const groups = new Map<string, T[]>();
  for (const r of rows) {
    const k = fmt.format(new Date(startsOf(r)));
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  return [...groups.entries()];
}

/** The column names, once, above the first month. */
export function SessionHead({ last, menu }: { last: string; menu?: boolean }) {
  const t = useT();
  return (
    <div className={`shead${menu ? ' shead--menu' : ''}`} aria-hidden="true">
      <span>{t('co.tbl.day')}</span>
      <span>{t('co.tbl.time')}</span>
      <span>{t('co.tbl.session')}</span>
      <span className="shead__end">{last}</span>
    </div>
  );
}

/**
 * One session as one row of four columns: day, time, what it was, and
 * the one thing still to know about it — the price ahead of time, the
 * rating afterwards. Every row lines up with the one above it, so the
 * eye can run down a column: all the times, or everything not rated.
 */
export function SessionRow({ starts, title, kind, people, series, missed, end, onOpen, menu }: {
  starts: string;
  title: string;
  kind?: string | null;
  people?: number;
  series?: boolean;
  /** A no-show: the session is struck through and says so. */
  missed?: boolean;
  end?: ReactNode;
  onOpen?: () => void;
  menu?: ReactNode;
}) {
  const t = useT();
  const d = new Date(starts);
  const weekday = new Intl.DateTimeFormat(localeOf(getLang()), { weekday: 'short' }).format(d);
  const group = kind === 'group' || (people ?? 0) > 1;
  const label = (people ?? 0) > 1 ? t('co.pp.groupOf', { n: people! }) : shortType(title);
  return (
    <div className={`srow${menu ? ' srow--menu' : ''}${missed ? ' srow--missed' : ''}`}>
      {onOpen && <button className="srow__open" aria-label={`${weekday} ${d.getDate()}, ${label}`} onClick={onOpen} />}
      <span className="srow__day">
        <span className="srow__wd">{weekday}</span>
        <span className="srow__dn">{d.getDate()}</span>
      </span>
      <span className="srow__time">{fmtTime(starts)}</span>
      <span className="srow__what">
        {group ? <GroupIcon /> : <PersonIcon />}
        <span className="srow__title">{label}</span>
        {series && <i className="srow__series" role="img" aria-label={t('co.profile.series')}>↻</i>}
      </span>
      <span className="srow__end">{end}</span>
      {menu}
    </div>
  );
}
